import { useMemo } from "react";

import type { StepsDataPoint } from "~/api/steps";

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

interface UseStepsChartDataProps {
  data: StepsDataPoint[];
  selectedRange: string;
  navigationOffset: number;
}

interface UseStepsChartDataReturn {
  chartData: ChartDataPoint[];
  highChartData: ChartDataPoint[];
  lowChartData: ChartDataPoint[];
}

export const useStepsChartData = ({
  data,
  selectedRange,
}: UseStepsChartDataProps): UseStepsChartDataReturn => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    if (selectedRange === "1d") {
      // For daily view, use hour as x-axis
      return data.map((item) => {
        const date = new Date(item.date);
        const totalSteps = item.walking + item.running;
        return {
          x: date.getHours(),
          y: totalSteps,
        };
      });
    } else {
      // For week/month/year views, group by day and sum steps
      const groupedByDay = new Map<string, number>();

      data.forEach((item) => {
        const date = new Date(item.date);
        // Create a date key (YYYY-MM-DD) for grouping using local date to avoid timezone shift
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        const totalSteps = item.walking + item.running;

        if (groupedByDay.has(dateKey)) {
          groupedByDay.set(dateKey, groupedByDay.get(dateKey)! + totalSteps);
        } else {
          groupedByDay.set(dateKey, totalSteps);
        }
      });

      // Convert grouped data to chart format
      return Array.from(groupedByDay.entries())
        .map(([dateKey, totalSteps]) => {
          const [year, month, day] = dateKey.split("-").map(Number);
          const date = new Date(Date.UTC(year, month - 1, day)); // month is 0-indexed
          return {
            x: date,
            y: totalSteps,
          };
        })
        .sort((a, b) => {
          // Sort by date
          if (a.x instanceof Date && b.x instanceof Date) {
            return a.x.getTime() - b.x.getTime();
          }
          return 0;
        });
    }
  }, [data, selectedRange]);

  const highChartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    if (selectedRange === "1d") {
      // For daily view, use hour as x-axis
      return data.map((item) => {
        const date = new Date(item.date);
        return {
          x: date.getHours(),
          y: item.running,
        };
      });
    } else {
      // For week/month/year views, group by day and sum running steps
      const groupedByDay = new Map<string, number>();

      data.forEach((item) => {
        const date = new Date(item.date);
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        if (groupedByDay.has(dateKey)) {
          groupedByDay.set(dateKey, groupedByDay.get(dateKey)! + item.running);
        } else {
          groupedByDay.set(dateKey, item.running);
        }
      });

      return Array.from(groupedByDay.entries())
        .map(([dateKey, runningSteps]) => {
          const [year, month, day] = dateKey.split("-").map(Number);
          const date = new Date(Date.UTC(year, month - 1, day)); // month is 0-indexed
          return {
            x: date,
            y: runningSteps,
          };
        })
        .sort((a, b) => {
          if (a.x instanceof Date && b.x instanceof Date) {
            return a.x.getTime() - b.x.getTime();
          }
          return 0;
        });
    }
  }, [data, selectedRange]);

  const lowChartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    if (selectedRange === "1d") {
      // For daily view, use hour as x-axis
      return data.map((item) => {
        const date = new Date(item.date);
        return {
          x: date.getHours(),
          y: item.walking,
        };
      });
    } else {
      // For week/month/year views, group by day and sum walking steps
      const groupedByDay = new Map<string, number>();

      data.forEach((item) => {
        const date = new Date(item.date);
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        if (groupedByDay.has(dateKey)) {
          groupedByDay.set(dateKey, groupedByDay.get(dateKey)! + item.walking);
        } else {
          groupedByDay.set(dateKey, item.walking);
        }
      });

      return Array.from(groupedByDay.entries())
        .map(([dateKey, walkingSteps]) => {
          const [year, month, day] = dateKey.split("-").map(Number);
          const date = new Date(Date.UTC(year, month - 1, day)); // month is 0-indexed
          return {
            x: date,
            y: walkingSteps,
          };
        })
        .sort((a, b) => {
          if (a.x instanceof Date && b.x instanceof Date) {
            return a.x.getTime() - b.x.getTime();
          }
          return 0;
        });
    }
  }, [data, selectedRange]);

  return {
    chartData,
    highChartData,
    lowChartData,
  };
};
